import React, { FC, useState } from 'react';
import { usePlacesWidget } from "react-google-autocomplete";
import { GOOGLE_MAP_API_KEY } from "../../data/price";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from '../../hooks';
import store from '../../store';
import { setDPDAddress as setDPDAddressRedux } from '../../stateReducers/deliverySlice';
import { belgiumRegexString, isBelgianAddress } from '../utils/stateHelper';

interface ValidatedAddressProps{
    label: string,
    address: string | null | undefined,
    addressValid: boolean,
    setAddress: (address: string | undefined) => void,
    setAddressValid: (inProgress: boolean) => void,
    mustBeBelgianAddress: boolean,
    mustHaveStreetNumber: boolean,
    wallonianAddressesAllowed: boolean,
}


const ValidatedAddressField: FC<ValidatedAddressProps> = ({
    label,
    address,
    setAddress,
    addressValid,
    setAddressValid,
    mustBeBelgianAddress,
    mustHaveStreetNumber = true,
    wallonianAddressesAllowed = false,
}) => {
    const {t} = useTranslation();
    const displayAddress = address ?? "";
    const [value, setValue] = useState(displayAddress);
    const [streetNumberError, setStreetNumberError] = useState<boolean>(false);
    const [awaitingGoogleResponse, setAwaitingGoogleResponse] = useState<boolean>(false);
    const [nonBelgianAddress, setNonBelgianAddressFlag] = useState<boolean>(false);
    const [displayAddressWarning, setDisplayAddressWarning] = useState<boolean>(false);
    const [suggestedAddress, setSuggestedAddress] = useState<string>("");
    const [wallonianAddressFlag, setWallonianAddressFlag] = useState<boolean>(false);
    let currentPlace: google.maps.places.PlaceResult | null = null;

    const {ref, autocompleteRef} = usePlacesWidget<HTMLInputElement>({
        apiKey: GOOGLE_MAP_API_KEY,
        onPlaceSelected: (place, ref, autocompleteRef) => {
            currentPlace = place;
            console.log(`onPlaceSelected triggered`);
            console.log('selected place', place, ref, autocompleteRef);
            if (place.formatted_address) {
                handlePlaceCheck(place, true);
            }
        },
        options:{
            types:['address']
        }
    });
    const addressHasHouseNumber = (components:  google.maps.GeocoderAddressComponent[]) => {
        return components.some((component) => {
           return component.types.some(type => {
               return type === 'street_number';
           })
        });
    }
    const processAddressChange = (value: string) => {
        setValue((value));
        setAddress(value);
    }

    function autofix(address: string) {
        console.log("autofix called");
        if (addressValid) {
            return;
        }
        const geocode_url = "https://maps.googleapis.com/maps/api/geocode/json?key=" + GOOGLE_MAP_API_KEY
            + (mustBeBelgianAddress ? "&components=country:BE" : "")
            + "&address=" + encodeURIComponent(address);
        console.log(geocode_url);
        setAwaitingGoogleResponse(true);
        fetch(geocode_url).then((res) => res.json())
        .then((json) => {
            console.log("Google Maps response json", json);
            // If Google Maps detects more than one possibility, then the customer must select place
            // via the Google Maps autocomplete dropdown or type more
            if (json.results && json.results.length && json.results.length === 1) {
                const resultPlace = json.results[0];
                handlePlaceCheck(resultPlace, false);
            }
            setAwaitingGoogleResponse(false);
        })
        .catch((error) => {
            setAwaitingGoogleResponse(false);
            throw error;
        });
    }

    /**
     * Checks that a Google Place meets our criteria and handles and relevant side-effects
     * 
     * @param place A Google Maps place object
     */
    function handlePlaceCheck(place: any, autoreplace: boolean): void {
        console.log("handlePlaceCheck place", place);
        if (
            mustBeBelgianAddress
            // Formatted address == Belgium means it couldn't resolve to address in Belgium, so is a non-Belgian address
            && place.formatted_address && place.formatted_address.match(new RegExp("^(" + belgiumRegexString + ")$"))
        ) {
            setNonBelgianAddressFlag(true);
            if (setAddressValid) {
                setAddressValid(false);
            }
        } else if (
            !wallonianAddressesAllowed
            && (place.address_components[4]['long_name'] == "Région Wallonne" || place.address_components[4]['long_name'] == "Waals Gewest")
        ) {
            setWallonianAddressFlag(true);
            if (setAddressValid) {
                setAddressValid(false);
            }
        } else {
            setNonBelgianAddressFlag(false);
            setWallonianAddressFlag(false);
            if(!mustHaveStreetNumber || addressHasHouseNumber(place.address_components || [])) {
                setStreetNumberError(false);
                console.log('Handling place check');
                console.log("Place address: " + place.formatted_address);
                setAddressOrSuggested(place.formatted_address || "", autoreplace);
            } else {
                console.log("Street number error")
                setStreetNumberError(true);
                // setValue(place.formatted_address || "");
                if (setAddressValid) {
                    setAddressValid(false);
                }
            }
        }
    }

    function setAddressOrSuggested(address: string, autoreplace: boolean) {
        if (autoreplace && address.length > 0) {
            console.log("Auto setting address: " + address);
            setAddress(address);
            setValue(address);
            setAddressValid(true);
        } else {
            console.log("Setting suggested address: " + address);
            setSuggestedAddress(address);
        }
    }

    function processKeyDown(e: React.FormEvent<HTMLInputElement>) {
        const innocentKeys = [
            'Dead',
            'Control',
            'Alt',
            'AltGraph',
            'CapsLock',
            'Fn',
            'FnLock',
            'Hyper',
            'Meta',
            'NumLock',
            'ScrollLock',
            'Shift',
            'Super',
            'Symbol',
            'SymbolLock',
            'OS',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'End',
            'Home',
            'PageDown',
            'PageUp',
            'BrightnessDown',
            'BrightnessUp',
            'Eject',
            'PrintScreen',
            'Hibernate',
            'LogOff',
            'Power',
            'PowerOff',
            'Standby',
            'WakeUp',
            'Tab',
        ]
        // Add all possible F# keys
        .concat([...Array(20).keys()].map(n => "F" + (n+1).toString()));
        if ('key' in e) {
            // Make exceptions for some innocent keystrokes and copying. Else, invalidate address
            if (!(
                typeof(e.key) === 'string'
                && (innocentKeys.includes(e.key) || (e.key.toLowerCase() === "c" && 'ctrlKey' in e && e.ctrlKey))
            )) {
                if (e.key === 'Enter') {
                    autofix(value);
                } else {
                    if (setAddressValid) {
                        setAddressValid(false);
                    }
                    setStreetNumberError(false);
                    setNonBelgianAddressFlag(false);
                    setDisplayAddressWarning(false);
                }
            }
        } else {
            if (setAddressValid) {
                setAddressValid(false);
            }
            setStreetNumberError(false);
            setNonBelgianAddressFlag(false);
            setDisplayAddressWarning(false);
        }
    }

    function onBlur() {
        setTimeout(() => {
            if (!addressValid) {
                setDisplayAddressWarning(true);
            }
        }, 2000);
    }
    
    const displayBelgianAddressError = useAppSelector((state) => {
        // If the address field is empty, don't show any error
        if (state.delivery.homeAddress !== undefined && state.delivery.homeAddress !== null
            && state.delivery.homeAddress === "") {
            return false;
        }
        // Don't prematurely show an error before Google's response
        if (awaitingGoogleResponse) {
            return false;
        }
        // If it's been flagged as a non-Belgian address from Google's response, return that
        // Else, do a double check for a Belgian address in the case that the address has been
        // flagged as otherwise valid
        return nonBelgianAddress
            || (
                state.delivery.homeAddress
                && addressValid
                && !isBelgianAddress(state.delivery.homeAddress));
    });

    const [promptAddressCheck, setPromptAddressCheck] = useState<boolean>(false);
    // const modalClassName = promptAddressCheck ? "modal-background" : "hidden";

    function acceptSuggestedAddress() {
        setAddressOrSuggested(suggestedAddress, true);
        setPromptAddressCheck(false);
    }

    function rejectSuggestedAddress() {
        setSuggestedAddress("");
        setPromptAddressCheck(false);
    }

    return (
        <>
        {promptAddressCheck &&
            <div className='modal-background'>
                <div className="promo-box" onClick={() => {return} }>
                    <p>{t('delivery.addressCheckPrompt')}</p>
                    <p><strong>{t('delivery.typedAddress')}:</strong> {value}</p>
                    <p><strong>{t('delivery.suggestedAddress')}:</strong> {suggestedAddress}</p>

                    <span
                        className='close close-green'
                        onClick={acceptSuggestedAddress}
                    >{t('delivery.acceptSuggestedAddress')}</span>
                    <span
                        className='close close-red'
                        onClick={rejectSuggestedAddress}
                    >{t('delivery.rejectSuggestedAddress')}</span>
                </div>
            </div>
        }
        {!addressValid && displayAddressWarning && (wallonianAddressesAllowed || !wallonianAddressFlag) &&
            <h3 style={{color: '#DD4400', margin: 5}}>{t('delivery.fixAddressWarning')}</h3>
        }
        {!wallonianAddressesAllowed && wallonianAddressFlag &&
            <h3 style={{color: '#DD4400', margin: 5}}>{t('delivery.wallonianAddressWarning')}</h3>
        }
        {mustBeBelgianAddress && displayBelgianAddressError &&
            <h3 style={{color: '#DD4400', margin: 5}}>{t('delivery.belgianAddressRequired')}</h3>
        }
        {streetNumberError &&
            <h3 style={{color: '#DD4400', margin: 5}}>{t('delivery.streetNumberRequired')}</h3>
        }
        <div className={`mb-3 xl:w-full ${streetNumberError ? " required-highlight" : ""}`}>
            <label htmlFor="validatedAddressField"
                   className={"form-label inline-block mb-2 text-gray-700"}
                   style={{width: '100%'}}>{label}
                <span
                    className="text-red-500 font-bold text-2xl">*</span>
            <input
                type="text"
                ref={ref}
                autoComplete="OFF"
                className="
                            form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding
                            border border-solid border-gray-300 rounded m-0
                            focus:shadow-none
                            focus:text-gray-700 focus:bg-white focus:border-amber-400 focus:outline-none
                          "
                value={value}
                onChange={(e) => processAddressChange(e.target.value)}
                onKeyDown={(event => processKeyDown(event))}
                onPaste={event => processKeyDown(event)}
                id="validatedAddressField"
                placeholder={t('delivery.dpdAddressPlaceholder')}
                onBlur={(e) => onBlur()}
            />
            </label>
            <button
                disabled={addressValid}
                onClick={(e) => {
                    autofix(value);
                    if (suggestedAddress !== value) {
                        setPromptAddressCheck(true);
                    } else {
                        setAddressOrSuggested(value, true);
                    }
            }} className={'button' + (addressValid ? ' button-address-verified' : '')}>{addressValid ? t('delivery.addressValid') : t('delivery.autofixAddress')}</button>

        </div>
        </>
    );
};

export default ValidatedAddressField;
